/**
 * ThresholdSliders Component
 *
 * Confidence threshold sliders for each Persona Engine engine (0.0-1.0, step 0.05).
 * Pre-filled with spec defaults. Color-coded: green (>= default),
 * yellow (below default), red (< 0.25). Per-slider reset to default.
 */

import { useCallback } from 'react'
import { motion } from 'framer-motion'
import { Sliders, RotateCcw } from 'lucide-react'
import type { EngineType } from '@personic/shared/types/persona-engine'
import { ENGINE_SHORT_LABELS } from '@personic/shared/types/persona-engine'

// ─── Defaults ────────────────────────────────────────────────────────────────

const SPEC_DEFAULTS: Record<string, number> = {
  cat: 0.35,
  ipc: 0.45,
  mi: 0.45,
  rft: 0.50,
  scarf: 0.50,
  aic: 0.55,
}

const ENGINE_ORDER: EngineType[] = ['cat', 'ipc', 'mi', 'rft', 'scarf', 'aic']

// ─── Types ───────────────────────────────────────────────────────────────────

interface ThresholdSlidersProps {
  thresholds: Record<string, number>
  defaults?: Record<string, number>
  onChange: (thresholds: Record<string, number>) => void
  disabled?: boolean
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSliderColor(value: number, defaultValue: number): {
  track: string
  text: string
  bg: string
  ring: string
} {
  if (value < 0.25) {
    return {
      track: 'bg-red-500',
      text: 'text-red-700',
      bg: 'bg-red-50',
      ring: 'ring-red-200',
    }
  }
  if (value < defaultValue) {
    return {
      track: 'bg-amber-500',
      text: 'text-amber-700',
      bg: 'bg-amber-50',
      ring: 'ring-amber-200',
    }
  }
  return {
    track: 'bg-emerald-500',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    ring: 'ring-emerald-200',
  }
}

function getSliderAccentVar(value: number, defaultValue: number): string {
  if (value < 0.25) return '#ef4444'
  if (value < defaultValue) return '#f59e0b'
  return '#22c55e'
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ThresholdSliders({
  thresholds,
  defaults = SPEC_DEFAULTS,
  onChange,
  disabled = false,
}: ThresholdSlidersProps) {
  const handleSliderChange = useCallback(
    (engine: string, newValue: number) => {
      onChange({ ...thresholds, [engine]: newValue })
    },
    [thresholds, onChange]
  )

  const handleReset = useCallback(
    (engine: string) => {
      const defaultVal = defaults[engine] ?? SPEC_DEFAULTS[engine] ?? 0.5
      onChange({ ...thresholds, [engine]: defaultVal })
    },
    [thresholds, defaults, onChange]
  )

  const handleResetAll = useCallback(() => {
    const resetThresholds: Record<string, number> = {}
    for (const engine of ENGINE_ORDER) {
      resetThresholds[engine] = defaults[engine] ?? SPEC_DEFAULTS[engine] ?? 0.5
    }
    onChange(resetThresholds)
  }, [defaults, onChange])

  const hasAnyModification = ENGINE_ORDER.some((engine) => {
    const current = thresholds[engine] ?? SPEC_DEFAULTS[engine]
    const defaultVal = defaults[engine] ?? SPEC_DEFAULTS[engine]
    return Math.abs(current - defaultVal) > 0.001
  })

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sliders className="w-5 h-5 text-personic-600" />
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Confidence Thresholds
          </h3>
        </div>

        {hasAnyModification && !disabled && (
          <button
            type="button"
            onClick={handleResetAll}
            className="flex items-center gap-1.5 text-xs font-medium text-personic-600 hover:text-personic-700 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset all
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          At or above default
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          Below default
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
          Critical (&lt; 0.25)
        </span>
      </div>

      {/* Sliders */}
      <div className="space-y-3">
        {ENGINE_ORDER.map((engine, index) => {
          const currentValue = thresholds[engine] ?? SPEC_DEFAULTS[engine] ?? 0.5
          const defaultVal = defaults[engine] ?? SPEC_DEFAULTS[engine] ?? 0.5
          const isModified = Math.abs(currentValue - defaultVal) > 0.001
          const colors = getSliderColor(currentValue, defaultVal)
          const accentColor = getSliderAccentVar(currentValue, defaultVal)

          return (
            <motion.div
              key={engine}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.25 }}
              className={`
                p-4 rounded-xl border transition-colors duration-150
                ${isModified ? `${colors.bg} border-transparent ring-1 ${colors.ring}` : 'border-gray-200 bg-white'}
              `}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">
                    {ENGINE_SHORT_LABELS[engine]}
                  </span>
                  {isModified && (
                    <span className={`text-xs font-medium ${colors.text}`}>
                      Modified
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={`text-sm font-mono font-semibold ${colors.text}`}
                  >
                    {currentValue.toFixed(2)}
                  </span>

                  {isModified && !disabled && (
                    <button
                      type="button"
                      onClick={() => handleReset(engine)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-personic-600 transition-colors"
                      title={`Reset to default (${defaultVal.toFixed(2)})`}
                    >
                      <RotateCcw className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Slider */}
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={currentValue}
                  onChange={(e) => handleSliderChange(engine, parseFloat(e.target.value))}
                  disabled={disabled}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${currentValue * 100}%, #e5e7eb ${currentValue * 100}%, #e5e7eb 100%)`,
                    accentColor,
                  }}
                />

                {/* Default marker */}
                <div
                  className="absolute top-0 w-0.5 h-2 bg-gray-400 pointer-events-none"
                  style={{ left: `${defaultVal * 100}%` }}
                  title={`Default: ${defaultVal.toFixed(2)}`}
                />
              </div>

              {/* Scale labels */}
              <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-0.5">
                <span>0.00</span>
                <span className="text-gray-500 font-medium">
                  Default: {defaultVal.toFixed(2)}
                </span>
                <span>1.00</span>
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
